import React from 'react'
import { useNavigate } from 'react-router-dom'
import { urlForTeam } from '~/utils/PlaceManager'
import { DEFAULT_TEAM_PAGE_TAB, TEAM_PAGE_TABS } from '~/react/tabs'
import { Employee } from '~/app/types'
import { Button, ButtonGroup } from 'react-bootstrap'
import { anyInManagementChain, isInManagementChain } from '~/react/employee-check-in/EmployeeCheckInSortUtil'
import { OWAuthDataModel } from '~/models/OWAuthDataModel'
import { authHasPermission, Permission } from '~/utils/Permissions'
import { isCheckInEnabled, isTimeOffEnabled } from '~/utils/OWUtils'

const isSeniorAdministrator = (forEmployee: Employee) => {
    const authEmployee = OWAuthDataModel.getAuthEmployee()
    return (
        authEmployee?.senior_administrator_managers?.includes(forEmployee.id) ||
        anyInManagementChain(forEmployee.lineage ?? '', authEmployee?.senior_administrator_managers ?? [])
    )
}
export const showTeamCheckInLink = (employee: Employee) => {
    const isCheckInAdmin = authHasPermission(Permission.CHECKIN) || isSeniorAdministrator(employee)
    const inChain = isInManagementChain(employee.lineage ?? '', OWAuthDataModel.getAuthEmployeeId()!)
    return (isCheckInAdmin || inChain) && isCheckInEnabled()
}

export const showTeamManagerFeedbackLink = (employee: Employee) => {
    const isManagerFeedbackAdmin =
        authHasPermission(Permission.MANAGER_FEEDBACK_VIEW) || isSeniorAdministrator(employee)
    const inChain = isInManagementChain(employee.lineage ?? '', OWAuthDataModel.getAuthEmployeeId()!)
    return isManagerFeedbackAdmin || inChain
}

export const showTeamTimeOffLink = isTimeOffEnabled()

export const EmployeeTeamLinks = ({ currentTab, employee }: { currentTab: string; employee: Employee }) => {
    const navigate = useNavigate()
    const [membersUrl, timeOffUrl, checkInUrl, feedbackUrl] = [
        TEAM_PAGE_TABS.members,
        TEAM_PAGE_TABS.timeOff,
        TEAM_PAGE_TABS.checkIn,
        TEAM_PAGE_TABS.managerFeedback,
    ].map(v => urlForTeam({ v, employeeSlug: employee.public_id }))

    const isMembersSelected = currentTab === TEAM_PAGE_TABS.members
    const isTimeOffSelected = currentTab === TEAM_PAGE_TABS.timeOff
    const isCheckInSelected = currentTab === TEAM_PAGE_TABS.checkIn || currentTab === TEAM_PAGE_TABS.checkInSummary
    const isFeedbackSelected = currentTab === TEAM_PAGE_TABS.managerFeedback

    return (
        <ButtonGroup className="button-tabs team-tabs no-extra-style">
            <Button
                className="team-tab"
                variant="outline-primary"
                active={isMembersSelected}
                onClick={() => void navigate(membersUrl)}
            >
                <span>Members</span>
            </Button>
            {showTeamTimeOffLink && (
                <Button
                    className="team-tab"
                    active={isTimeOffSelected}
                    variant="outline-primary"
                    onClick={() => void navigate(timeOffUrl)}
                >
                    <span>Time Off</span>
                </Button>
            )}
            {showTeamCheckInLink(employee) && (
                <Button
                    className="team-tab"
                    active={isCheckInSelected}
                    variant="outline-primary"
                    onClick={() => void navigate(checkInUrl)}
                >
                    <span>Check In</span>
                </Button>
            )}
            {showTeamManagerFeedbackLink(employee) && (
                <Button
                    className="team-tab"
                    active={isFeedbackSelected}
                    variant="outline-primary"
                    onClick={() => void navigate(feedbackUrl)}
                >
                    <span>Manager Feedback</span>
                </Button>
            )}
        </ButtonGroup>
    )
}

export const getTeamTab = (v: string) => {
    const tabs = TEAM_PAGE_TABS
    const isValid = Object.keys(tabs)
        .map(k => tabs[k])
        .includes(v)
    return isValid ? v : DEFAULT_TEAM_PAGE_TAB
}
